import type { KeyboardEvent, MouseEvent } from 'react';
import styled from 'styled-components';
import { useTranslation } from '@/app/components/translations/Translation';
import { Icon } from '@/app/common/icons/Icon';
import { MEAL_PLAN_INCLUDED_OPTIONS_TITLE_KEY } from './mealPlanIncludedOptions';

// Inline, text-sized trigger that sits right after the meal plan name. `pointer-events: auto`
// re-enables clicks inside the legacy radio label, which switches pointer events off as a whole so
// the option row handles the selection click itself.
const InfoButton = styled.button`
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  margin-left: 4px;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  pointer-events: auto;
`;

type MealPlanIncludedOptionsInfoButtonProps = {
  onClick: () => void;
  size?: number;
};

/**
 * Legacy room area info icon for the meal plan's included options (MPH-946). It lives inside
 * clickable rows (the meal plan radio options, the room card summary), so activation — by pointer
 * or keyboard — is stopped here and never reaches the row's own select handler.
 */
export function MealPlanIncludedOptionsInfoButton({ onClick, size = 16 }: MealPlanIncludedOptionsInfoButtonProps) {
  const { t } = useTranslation();

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onClick();
  };

  // Both key phases have to be stopped, on both keys. The button's own activation arrives as a click
  // (the browser dispatches it on Enter keydown and on Space keyup), so stopping propagation here
  // never blocks it — but letting either phase through activates the row underneath: the meal plan
  // radio rows select on keydown, while the legacy mobile room card is a role="button" wired to
  // onKeyUp (keyUpHandler), which used to open the room-details modal on top of this one.
  const stopRowActivation = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.stopPropagation();
    }
  };

  return (
    <InfoButton
      type="button"
      aria-label={t(MEAL_PLAN_INCLUDED_OPTIONS_TITLE_KEY)}
      data-testid="meal-plan-included-options-info"
      onClick={handleClick}
      onKeyDown={stopRowActivation}
      onKeyUp={stopRowActivation}
    >
      <Icon category="Semantics" name="icon-circle-info" size={size} color="iconSecondary" alt="" />
    </InfoButton>
  );
}
